import React, { useState } from 'react';
import { FiX, FiPlus, FiDollarSign, FiCalendar, FiInfo, FiTrash2, FiTrendingUp, FiCheckCircle } from 'react-icons/fi';
import { STATUS } from '../constants/status';
import RegistrarPagamentoModal from './RegistrarPagamentoModal';
import importacaoService from '../services/importacaoService';
import toast from 'react-hot-toast';

export default function ImportacaoDetalhesModal({ isOpen, onClose, importacao, onUpdate }) {
  const [isPagamentoModalOpen, setIsPagamentoModalOpen] = useState(false);

  if (!isOpen || !importacao) return null;

  const formatarMoeda = (valor, moeda = 'BRL') => {
    if (valor === null || valor === undefined) return '—';
    if (moeda === 'BRL') {
      return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }
    return `${moeda} ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  const formatarData = (data) => {
    if (!data) return '—';
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const formatarTipoPagamento = (tipo) => {
     const mapas = {
        SINAL_30: "Sinal (30%)",
        SALDO_70: "Saldo (70%)",
        IMPOSTOS_DESEMBARACO: "Impostos / Custo Nac.",
        FINIMP_30_PARCELA: "Parcela Finimp (Entrada)",
        FINIMP_70_PARCELA: "Parcela Finimp (Saldo)",
        FRETE_OUTROS: "Frete / Outros Custos"
     };
     return mapas[tipo] || "Pagamento";
  };

  const handleDeletarPagamento = async (pagamentoId) => {
    if (window.confirm('Excluir este registro de pagamento?')) {
      try {
        await importacaoService.deletarPagamento(importacao.id, pagamentoId);
        toast.success('Pagamento removido!');
        onUpdate();
      } catch (error) {
        toast.error('Erro ao excluir pagamento');
      }
    }
  };

  const handleLiquidar = async () => {
    if (window.confirm('Deseja forçar a liquidação final desta operação?')) {
      try {
        await importacaoService.liquidar(importacao.id);
        toast.success('Operação Liquidada com sucesso!');
        onUpdate();
      } catch(e) {
        toast.error('Erro ao liquidar operação');
      }
    }
  };

  const totalOperacao = importacao.totalReal || importacao.totalProjecao || 0;
  const totalPagamentosExtras = importacao.pagamentos?.reduce((acc, p) => acc + p.valor, 0) || 0;
  const totalPagoAteAgora = (importacao.valorPago30 || 0) + totalPagamentosExtras;
  const saldoDevedor = totalOperacao - totalPagoAteAgora;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex justify-center items-center p-4 backdrop-blur-md pt-10 pb-10">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-300">
        
        {/* HEADER */}
        <div className="bg-white px-8 py-6 border-b flex justify-between items-center shrink-0 z-10 shadow-sm">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-2xl text-white shadow-lg ${importacao.status === STATUS.LIQUIDADA ? 'bg-green-500 shadow-green-200' : 'bg-blue-600 shadow-blue-200'}`}>
              {importacao.status === STATUS.LIQUIDADA ? <FiCheckCircle size={24} /> : <FiTrendingUp size={24} />}
            </div>
            <div>
              <h2 className="text-2xl font-black text-gray-900">{importacao.produto}</h2>
              <p className="text-sm font-bold text-gray-500 flex items-center gap-1 mt-1">
                 {importacao.fornecedor || 'Fornecedor não informado'}
                 {importacao.sku && ` • SKU: ${importacao.sku}`}
              </p>
              <div className="flex items-center gap-3 mt-2">
                <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold inline-flex items-center gap-1 border ${
                  importacao.status === STATUS.LIQUIDADA ? 'bg-green-100 text-green-800 border-green-200' :
                  importacao.status === STATUS.DESEMBARACADO ? 'bg-blue-100 text-blue-800 border-blue-200' : 'bg-amber-100 text-amber-800 border-amber-200'
                }`}>
                  {importacao.status === STATUS.LIQUIDADA && <FiCheckCircle />}
                  {importacao.status}
                </span>

                {importacao.invoiceUrl && (
                  <button 
                    onClick={() => window.open(importacao.invoiceUrl, '_blank')}
                    className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 text-xs font-bold rounded-lg border border-gray-300 transition-colors shadow-sm ml-2"
                  >
                    📄 Visualizar Invoice
                  </button>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-900 rounded-xl transition-all hover:bg-gray-100">
            <FiX size={28} />
          </button>
        </div>

        <div className="p-8 grid grid-cols-1 lg:grid-cols-12 gap-8 overflow-y-auto flex-1">
          
          {/* LADO ESQUERDO: INFOS */}
          <div className="lg:col-span-8 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* PROJEÇÃO */}
              <div className="p-6 rounded-2xl border border-gray-100 bg-gray-50/30">
                <h3 className="text-xs font-black text-blue-600 uppercase tracking-widest mb-4 flex items-center gap-2"><FiCalendar /> Projeção de Entrada</h3>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <span className="text-gray-500">Valor Inicial:</span> <span className="font-bold text-right">{formatarMoeda(importacao.valorInicial, importacao.moeda)}</span>
                  <span className="text-gray-500">Câmbio Entrada:</span> <span className="font-bold text-right">R$ {importacao.taxaCambioEntrada?.toFixed(4)}</span>
                  <span className="text-gray-500">Data Base:</span> <span className="font-bold text-right">{formatarData(importacao.dataImportacao)}</span>
                  <div className="col-span-2 border-t border-dashed my-2"></div>
                  
                  <span className="text-gray-500">Juros Finimp (30%):</span> 
                  <span className="font-bold text-right text-orange-600">
                    {(importacao.possuiFinimp30 || importacao.possuiFinimpInicial) ? formatarMoeda(importacao.valorFinimp30 || importacao.valorFinimpInicial, 'BRL') : 'Não possui'}
                  </span>
                  {(importacao.possuiFinimp30 || importacao.possuiFinimpInicial) && (
                    <>
                      <span className="text-gray-400 italic text-xs pl-2">↳ Banco:</span> 
                      <span className="font-bold text-right italic text-xs text-gray-500">{importacao.bancoFinimp30 || importacao.bancoFinimpInicial || '—'}</span>
                      <span className="text-gray-400 italic text-xs pl-2">↳ Contratação:</span> 
                      <span className="font-bold text-right italic text-xs text-gray-500">{formatarData(importacao.dataContratacaoFinimp30 || importacao.dataContratacaoFinimpInicial)}</span>
                      <span className="text-gray-400 italic text-xs pl-2">↳ Quitação:</span> 
                      <span className="font-bold text-right italic text-xs text-gray-500">{formatarData(importacao.dataQuitacaoFinimp30 || importacao.dataQuitacaoFinimpInicial)}</span>
                    </>
                  )}
                  
                  <span className="text-gray-500 text-blue-600">Sinal (30%):</span> <span className="font-bold text-right text-blue-600">{formatarMoeda(importacao.valorPago30, 'BRL')}</span>
                  <span className="text-gray-500">Saldo (70%):</span> <span className="font-bold text-right">{formatarMoeda(importacao.projecao70Restante, 'BRL')}</span>
                  <span className="text-gray-500">Impostos (50%):</span> <span className="font-bold text-right">{formatarMoeda(importacao.projecao50Desembaraco, 'BRL')}</span>
                </div>
              </div>

              {/* DESEMBARAÇO */}
              <div className={`p-6 rounded-2xl border ${importacao.status === STATUS.DESEMBARACADO || importacao.status === STATUS.LIQUIDADA ? 'border-green-100 bg-green-50/30' : 'border-gray-100 bg-gray-50/30 opacity-60'}`}>
                <h3 className="text-xs font-black text-green-600 uppercase tracking-widest mb-4 flex items-center gap-2"><FiInfo /> Desembaraço Real</h3>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <span className="text-gray-500">Valor Final (Y):</span> <span className="font-bold text-right">{formatarMoeda(importacao.valorFinal, importacao.moeda)}</span>
                  <span className="text-gray-500">Câmbio Saída:</span> <span className="font-bold text-right">{importacao.taxaCambioDesembaraco ? `R$ ${importacao.taxaCambioDesembaraco.toFixed(4)}` : '—'}</span>
                  <span className="text-gray-500">Efetivado em:</span> <span className="font-bold text-right">{formatarData(importacao.dataDesembaraco)}</span>
                  <div className="col-span-2 border-t border-dashed my-2"></div>
                  
                  <span className="text-gray-500">Juros Finimp (70%):</span> 
                  <span className="font-bold text-right text-orange-600">
                    {(importacao.possuiFinimp70 || importacao.valorFinimp70 > 0) ? formatarMoeda(importacao.valorFinimp70, 'BRL') : '—'}
                  </span>
                  {(importacao.possuiFinimp70 || importacao.valorFinimp70 > 0) && (
                    <>
                      <span className="text-gray-400 italic text-xs pl-2">↳ Banco:</span> 
                      <span className="font-bold text-right italic text-xs text-gray-500">{importacao.bancoFinimp70 || '—'}</span>
                      <span className="text-gray-400 italic text-xs pl-2">↳ Contratação:</span> 
                      <span className="font-bold text-right italic text-xs text-gray-500">{formatarData(importacao.dataContratacaoFinimp70)}</span>
                      <span className="text-gray-400 italic text-xs pl-2">↳ Quitação:</span> 
                      <span className="font-bold text-right italic text-xs text-gray-500">{formatarData(importacao.dataQuitacaoFinimp70)}</span>
                    </>
                  )}
                  
                  <span className="text-gray-500">Real 70%:</span> <span className="font-bold text-right text-green-700">{formatarMoeda(importacao.real70Restante, 'BRL')}</span>
                  <span className="text-gray-500">Real 50%:</span> <span className="font-bold text-right text-green-700">{formatarMoeda(importacao.real50Desembaraco, 'BRL')}</span>
                </div>
              </div>
            </div>

            {/* TABELA DE HISTÓRICO */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="px-6 py-4 flex justify-between items-center border-b bg-gray-50/50">
                <h3 className="font-black text-gray-700 text-xs uppercase tracking-widest flex items-center gap-2"><FiDollarSign className="text-green-600" /> Histórico Financeiro</h3>
                {importacao.status !== STATUS.LIQUIDADA && (
                  <button onClick={() => setIsPagamentoModalOpen(true)} className="bg-gray-900 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-black transition-all flex items-center gap-2 shadow-md">
                    <FiPlus /> Registrar Pagamento
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    <tr className="bg-gray-50/20">
                      <td className="px-6 py-4">
                        <div className="font-bold text-gray-500 italic">Sinal Bancário de Abertura (30%)</div>
                        <div className="text-[10px] text-gray-400 uppercase">Processado na etapa de Entrada</div>
                      </td>
                      <td className="px-6 py-4"></td>
                      <td className="px-6 py-4 text-right font-bold text-gray-500">{formatarMoeda(importacao.valorPago30, 'BRL')}</td>
                      <td className="w-16"></td>
                    </tr>
                    {importacao.pagamentos?.map((p) => (
                      <tr key={p.id} className="hover:bg-red-50/30 group transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-bold text-gray-800">{formatarTipoPagamento(p.tipo)}</div>
                          <div className="text-[10px] font-mono text-gray-500 mt-0.5">{formatarData(p.dataPagamento)}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-xs text-gray-500 italic truncate max-w-[200px]">{p.observacao || '--'}</div>
                        </td>
                        <td className="px-6 py-4 text-right font-black text-green-600 whitespace-nowrap">{formatarMoeda(p.valor, 'BRL')}</td>
                        <td className="px-6 py-4 text-center">
                          {importacao.status !== STATUS.LIQUIDADA && (
                            <button onClick={() => handleDeletarPagamento(p.id)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all p-2 rounded-full hover:bg-red-50">
                              <FiTrash2 size={18} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* RESUMO (LADO DIREITO) */}
          <div className="lg:col-span-4 bg-gray-900 text-white rounded-[2.5rem] p-10 flex flex-col justify-between shadow-2xl relative overflow-hidden h-full">
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-[80px] -mr-32 -mt-32 pointer-events-none"></div>
            
            <div className="relative z-10">
              <h3 className="text-gray-400 uppercase text-[11px] font-black tracking-[0.3em] mb-12 flex items-center gap-2">
                Resumo Consolidado 
                {importacao.status === STATUS.LIQUIDADA && <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-[9px]">FINALIZADO</span>}
              </h3>
              <div className="space-y-10">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-black mb-2">Total da Operação Estimado</p>
                  <p className="text-2xl xl:text-3xl font-light truncate">{formatarMoeda(totalOperacao, 'BRL')}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-black mb-2">Total Já Efetivado</p>
                  <p className="text-2xl xl:text-3xl font-black text-green-400 truncate">{formatarMoeda(totalPagoAteAgora, 'BRL')}</p>
                </div>
              </div>
            </div>
            
            <div className="relative z-10 mt-16 pt-10 border-t border-gray-800/50 flex flex-col items-start gap-6">
              <div className="w-full">
                <p className="text-[10px] text-gray-500 uppercase font-black mb-2">Saldo em Aberto Estimado</p>
                <p className={`text-3xl xl:text-4xl font-black tracking-tighter break-all ${saldoDevedor <= 0 || importacao.status === STATUS.LIQUIDADA ? 'text-green-500' : 'text-orange-500'}`}>
                  {formatarMoeda(saldoDevedor, 'BRL')}
                </p>
              </div>

              {importacao.status !== STATUS.LIQUIDADA && (
                <button 
                  onClick={handleLiquidar}
                  className="w-full mt-4 py-3 px-4 border border-green-500/30 bg-green-500/10 hover:bg-green-500 text-green-400 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-lg active:scale-95"
                >
                  Liquidar Manualmente
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <RegistrarPagamentoModal isOpen={isPagamentoModalOpen} onClose={() => setIsPagamentoModalOpen(false)} importacaoId={importacao.id} onSucesso={onUpdate} />
    </div>
  );
}